import { FC, useMemo, MouseEvent } from 'react';
import BaseComment from './BaseComment';
import { TComment } from '../types';
import { TDocument, TDraft, TVersion, useScrollContent } from '@modules/documents';
import { TFile, TFileVersion } from '@modules/files';
import { TResolvedItemsMap, SCREEN_MODE_TO_PARAM_MAP, EItemScreenMode } from '@modules/items';
import { TPermissions } from '@modules/access';
import { Body1Strong, Caption1, Link as FluentLink } from '@fluentui/react-components';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { BranchCompareRegular } from '@fluentui/react-icons';
import { IconLabel } from '@shared/components';
import useCommentStore from '../stores/useCommentStore';
import { TInlineBlock, TInlineBlockDraft, TInlineBlockVersion } from '@modules/inlineBlocks';

type ImpactCommentProps = {
  comment: TComment;
  item: TDocument | TFile | TInlineBlock;
  version: TInlineBlockDraft | TDraft | TVersion | TFileVersion | TInlineBlockVersion;
  permissions: TPermissions;
  resolvedItems: TResolvedItemsMap;
};

const ImpactComment: FC<ImpactCommentProps> = ({ comment, resolvedItems, item, version, ...rest }) => {
  const scrollContent = useScrollContent();
  const { selectedCommentId } = useCommentStore();

  const handleScrollToHeading = (e: MouseEvent, hid: string) => {
    scrollContent({ value: hid });
    if (selectedCommentId === comment._id) e.stopPropagation();
  };

  const displayedContent = useMemo(() => {
    const { impact } = comment;
    if (!impact) return null;

    const { source } = impact;
    const resolvedSource = resolvedItems[source.item];

    const headings = (version as TDraft | TVersion).headings || [];
    const isSelected = selectedCommentId === comment._id;

    const linkToSourceItem = `/${source.itemType.toLocaleLowerCase()}/${source.item}?view=${source.versionId}&screenMode=${SCREEN_MODE_TO_PARAM_MAP[EItemScreenMode.IMPACT_ANALYSIS_SUMMARY]}`;

    return (
      // add some padding to the bottom of the comment if it is selected
      <div className={classNames(`flex flex-col`, { 'gap-2': impact.headingIds?.length, 'pb-2': isSelected })}>
        <p>
          Changes made in version {source.versionId} of
          <Link to={linkToSourceItem} target="_blank">
            <FluentLink>
              <Body1Strong>{resolvedSource ? resolvedSource.item.name : 'file (No access)'}</Body1Strong>
            </FluentLink>
          </Link>{' '}
          could potentially impact {impact.headingIds?.length ? 'the following sections:' : 'this file.'}
        </p>
        <ul className="pl-2">
          {impact.headingIds?.map((hid) => {
            const heading = headings.find((h) => h.id === hid);
            return (
              <li className="flex items-start gap-2">
                <span className={classNames('text-black', { 'text-brand-80': heading })}>•</span>
                {heading ? (
                  <FluentLink key={hid} onClick={(e) => handleScrollToHeading(e, hid)} aria-label="scroll to heading">
                    <Caption1>
                      {heading.numbering} {heading.text}
                    </Caption1>
                  </FluentLink>
                ) : (
                  <Caption1 key={hid}>Deleted section</Caption1>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    );
  }, [comment, resolvedItems, selectedCommentId, version]);

  return (
    <BaseComment
      header={
        <Caption1 className="text-neutral-600 mt-1">
          <IconLabel icon={<BranchCompareRegular />}>Auto-generated impact comment</IconLabel>
        </Caption1>
      }
      {...rest}
      item={item}
      comment={comment}
      displayedContent={displayedContent}
    />
  );
};

export default ImpactComment;
